import {
  UserSelectionInput,
  UserSelectionSetInput,
} from "@src/types.generated";
import { UserSelectionSetFragment } from "@src/features/messaging/SentMessage.generated";
import { useCallback, useMemo, useState } from "react";
import {
  isUserSelectionEmpty,
  isUserSelectionInputEmpty,
  userSelectionToUserSelectionInput,
} from "@src/features/contentLibrary/utils/assignments";

type Props = {
  initialUserSelectionSetFragment?: UserSelectionSetFragment | null;
  initialUserSelectionInput?: UserSelectionInput;
  initialUserSelectionSetInput?: UserSelectionSetInput | null;
  onChange?: (newInput: UserSelectionSetInput) => void;
};

export const getInitialUserSelections = (
  props: Pick<
    Props,
    | "initialUserSelectionSetFragment"
    | "initialUserSelectionInput"
    | "initialUserSelectionSetInput"
  >,
): UserSelectionInput[] => {
  if (props.initialUserSelectionInput) {
    return [props.initialUserSelectionInput];
  }
  if (props.initialUserSelectionSetInput) {
    return props.initialUserSelectionSetInput.userSelections;
  }
  return (props.initialUserSelectionSetFragment?.userSelections || [])
    .filter((us) => !isUserSelectionEmpty(us))
    .map(userSelectionToUserSelectionInput);
};

type UserSelectionSetInputState = {
  userSelectionSetInput: UserSelectionSetInput;
  setUserSelectionSetInput: (input: UserSelectionSetInput) => void;
  deleteUserSelection: (index: number) => void;
  editUserSelection: (index: number, selection: UserSelectionInput) => void;
  addUserSelection: (selection: UserSelectionInput) => void;
};

const useUserSelectionSetInputState = (
  props: Props,
): UserSelectionSetInputState => {
  const {
    onChange,
    initialUserSelectionInput,
    initialUserSelectionSetInput,
    initialUserSelectionSetFragment,
  } = props;
  const transformedInitialSelections = useMemo(
    () =>
      getInitialUserSelections({
        initialUserSelectionInput,
        initialUserSelectionSetInput,
        initialUserSelectionSetFragment,
      }),
    [
      initialUserSelectionInput,
      initialUserSelectionSetInput,
      initialUserSelectionSetFragment,
    ],
  );

  // Init-only from props; onChange callers own the state after mount.
  // Callers that need to reset when the source data changes (node
  // switch, refetched dialog, etc.) should key their component on the
  // relevant id.
  const [userSelectionSetInput, setUserSelectionSetInput] =
    useState<UserSelectionSetInput>({
      userSelections: transformedInitialSelections,
    });

  const deleteUserSelection = useCallback(
    (index: number) => {
      setUserSelectionSetInput((prev) => {
        const newInput = {
          userSelections: prev.userSelections.filter((_, i) => i !== index),
        };
        onChange?.(newInput);
        return newInput;
      });
    },
    [onChange],
  );

  const editUserSelection = useCallback(
    (index: number, selection: UserSelectionInput) => {
      setUserSelectionSetInput((prev) => {
        const currentSelection = prev.userSelections[index];
        const newInput = isUserSelectionInputEmpty(selection)
          ? {
              userSelections: prev.userSelections.filter((_, i) => i !== index),
            }
          : {
              userSelections: prev.userSelections.map((item, i) =>
                i === index ? { ...selection, id: currentSelection.id } : item,
              ),
            };
        onChange?.(newInput);
        return newInput;
      });
    },
    [onChange],
  );

  const addUserSelection = useCallback(
    (selection: UserSelectionInput) => {
      if (!isUserSelectionInputEmpty(selection)) {
        setUserSelectionSetInput((prev) => {
          const newInput = {
            userSelections: [...prev.userSelections, selection],
          };
          onChange?.(newInput);
          return newInput;
        });
      }
    },
    [onChange],
  );

  return useMemo(() => {
    return {
      userSelectionSetInput,
      setUserSelectionSetInput,
      deleteUserSelection,
      editUserSelection,
      addUserSelection,
    };
  }, [
    userSelectionSetInput,
    deleteUserSelection,
    editUserSelection,
    addUserSelection,
  ]);
};

export default useUserSelectionSetInputState;
